// src/pages/invoices/InvoiceList.tsx
import { Column, DataTable } from '@/components/common/DataTable';
import DeleteConfirmDialog from '@/components/common/DeleteConfirmDialog';
import SelectDropdown from '@/components/common/SelectDropdown';
import { SimpleTooltip } from '@/components/common/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import useDebounce from '@/hooks/use-debounce';
import { DEFAULT_PAGINATION } from '@/lib/constants/common';
import { invoiceSortOptions, invoiceStatusOptions } from '@/lib/constants/invoice';
import { bankDetailsAPI, companiesAPI, customersAPI, invoicesAPI, itemsAPI } from '@/lib/utils/api';
import { formatCurrency, formatDate } from '@/lib/utils/invoice';
import { Pagiantion } from '@/types/common.type';
import { Invoice, ItemRef } from '@/types/invoice.types';
import { Download, Edit, Eye, FileText, Info, Loader2, Plus, Trash2 } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { generatePDF, generatePDFBlob } from './PdfGenerator';
import { PDFFullPagePreview } from './components/PDFFullPagePreview';

const InvoiceList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<string>('all_status');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortValue, setSortValue] = useState<string>('invoiceDate-desc');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(null);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);

  const [showPreview, setShowPreview] = useState(false);
  const [pdfData, setPdfData] = useState<{ blob: Blob; fileName: string } | null>(null);

  const handlePreview = async (
    formData: any,
    items: any[],
    selectedCompany: any,
    selectedCustomer: any,
    selectedBank: any,
    options: { download?: boolean } = {}
  ) => {
    const result = await generatePDFBlob(
      formData,
      items,
      selectedCompany,
      selectedCustomer,
      selectedBank
    );
    if (result) {
      setPdfData(result);
      setShowPreview(true);
    }
  };

  // Get invoice amount
  const getInvoiceAmount = (inv: Invoice): number => {
    if (typeof inv.totalAmount === 'number') return inv.totalAmount;
    return (inv.items || []).reduce((sum, it) => sum + Number(it.amount || 0), 0);
  };

  // Fetch invoices
  const fetchInvoices = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await invoicesAPI.getAll({
        search: debouncedSearch || undefined,
        status: statusFilter !== 'all_status' ? statusFilter : undefined,
        page: currentPage,
        limit: rowsPerPage,
        sort: sortValue,
      });

      const raw: Invoice[] = response.data?.invoices || [];
      const {
        total,
        limit = rowsPerPage,
        page,
        pages,
      }: Pagiantion = response.data?.pagination ?? DEFAULT_PAGINATION;
      setInvoices(raw);
      setTotalInvoices(total);
    } catch {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, rowsPerPage, sortValue]);

  // Fetch whenever page OR filters change
  useEffect(() => {
    fetchInvoices();
  }, [currentPage, debouncedSearch, statusFilter, rowsPerPage, sortValue]);

  useEffect(() => {
    if ((location.state as { refresh?: boolean })?.refresh) {
      fetchInvoices();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Handle delete
  const handleDeleteClick = (invoice: Invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;

    try {
      setDeleting(true);
      await invoicesAPI.delete(invoiceToDelete._id);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch {
      toast.error('Failed to delete invoice');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  // Handle PDF generation
  const handleGeneratePDF = async (invoice: Invoice, download = false): Promise<void> => {
    if (download) {
      setDownloadLoadingId(invoice._id);
    } else {
      setPdfLoadingId(invoice._id);
    }
    try {
      const [companyRes, customerRes, bankRes] = await Promise.all([
        invoice.companyId?._id
          ? companiesAPI.getById(invoice.companyId._id)
          : Promise.resolve({ data: null }),
        invoice.customerId?._id
          ? customersAPI.getById(invoice.customerId._id)
          : Promise.resolve({ data: null }),
        // Only fetch bank details if selectedBankId exists and has _id
        invoice.selectedBankId?._id
          ? bankDetailsAPI.getById(invoice.selectedBankId._id)
          : Promise.resolve({ data: null }),
      ]);

      const patchedItems = await Promise.all(
        (invoice.items || []).map(async (item) => {
          if (typeof item.itemId === 'string') {
            try {
              const res = await itemsAPI.getById(item.itemId);
              return {
                ...item,
                description: res.data?.description || item.description,
                hsnSac: res.data?.hsnSac || (item?.itemId as ItemRef)?.hsnSac || '',
              };
            } catch {
              return item; // Return original item if fetch fails
            }
          }
          return item;
        })
      );

      if (!download) {
        await handlePreview(invoice, patchedItems, companyRes.data, customerRes.data, bankRes.data);
      } else {
        await generatePDF(invoice, patchedItems, companyRes.data, customerRes.data, bankRes.data, {
          download,
        });
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      if (download) {
        setDownloadLoadingId(null);
      } else {
        setPdfLoadingId(null);
      }
    }
  };

  // Handle edit
  const handleEditInvoice = async (id: string): Promise<void> => {
    try {
      const response = await invoicesAPI.getById(id);
      navigate(`/invoices/${id}/edit`, {
        state: { invoiceData: response.data, isEdit: true },
      });
    } catch {
      toast.error('Failed to load invoice');
    }
  };

  // Get customer display name
  const getCustomerName = (invoice: Invoice): string => {
    if (invoice.customerId?.companyName) return invoice.customerId.companyName;
    if (invoice.customerId?.primaryContact) return invoice.customerId.primaryContact;
    if (invoice.individualName) return invoice.individualName;
    return 'Unknown';
  };

  // Get status badge
  const getStatusBadge = (status: string, isDraft?: boolean) => {
    if (isDraft) {
      return (
        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
          Draft
        </Badge>
      );
    }

    const normalizedStatus = status?.toLowerCase();
    const statusStyles: Record<string, string> = {
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
    };

    return (
      <Badge
        variant="secondary"
        className={`capitalize ${statusStyles[normalizedStatus] || 'bg-gray-100 text-gray-800'}`}
      >
        {normalizedStatus
          ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
          : 'N/A'}
      </Badge>
    );
  };

  // Table columns
  const columns: Column<Invoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice Number',
      cell: (invoice) => <div className="font-medium">#{invoice.invoiceNumber}</div>,
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (invoice) => (
        <>
          <div className="font-medium whitespace-nowrap">{getCustomerName(invoice)}</div>
          {invoice.customerId?.companyName && invoice.customerId?.primaryContact && (
            <div className="text-sm text-muted-foreground">{invoice.customerId.primaryContact}</div>
          )}
        </>
      ),
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      cell: (invoice) => (
        <div className="text-sm">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '-'}</div>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      cell: (invoice) => (
        <div className="text-sm">{invoice.dueDate ? formatDate(invoice.dueDate) : '-'}</div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (invoice) => getStatusBadge(invoice.status, invoice.isDraft),
    },
    {
      key: 'amount',
      header: 'Amount',
      cell: (invoice) => (
        <div className="font-medium">
          {formatCurrency(getInvoiceAmount(invoice), invoice?.customerId?.currency)}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right sticky right-0',
      cell: (invoice) => (
        <div className="flex items-center justify-end gap-1">
          {!invoice.isDraft && (
            <>
              {/* Preview Button */}
              <SimpleTooltip content="Preview Invoice" sideOffset={2}>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleGeneratePDF(invoice)}
                    className="text-gray-600 hover:bg-gray-50 hover:text-gray-600"
                    disabled={pdfLoadingId === invoice._id}
                  >
                    {pdfLoadingId === invoice._id ? (
                      <Loader2 className="!h-[18px] !w-[18px] animate-spin" />
                    ) : (
                      <Eye className="!h-[18px] !w-[18px]" />
                    )}
                  </Button>
                </span>
              </SimpleTooltip>

              {/* Download Button */}
              <SimpleTooltip content="Download PDF">
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleGeneratePDF(invoice, true)}
                    className="text-green-600 hover:bg-green-50 hover:text-green-600"
                    disabled={pdfLoadingId === invoice._id}
                  >
                    {downloadLoadingId === invoice._id ? (
                      <Loader2 className="!h-[18px] !w-[18px] animate-spin" />
                    ) : (
                      <Download className="!h-[18px] !w-[18px]" />
                    )}
                  </Button>
                </span>
              </SimpleTooltip>
            </>
          )}

          {/* Edit Button */}
          <SimpleTooltip content="Edit Invoice">
            <span>
              <Button
                variant="ghost"
                size="icon"
                className="text-blue-600 hover:bg-blue-50 hover:text-blue-600"
                onClick={() => handleEditInvoice(invoice._id)}
              >
                <Edit className="!h-[18px] !w-[18px]" />
              </Button>
            </span>
          </SimpleTooltip>

          {/* Delete Button */}
          <SimpleTooltip content="Delete Invoice">
            <span>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-600 hover:bg-red-50 hover:text-red-600"
                onClick={() => handleDeleteClick(invoice)}
              >
                <Trash2 className="!h-[18px] !w-[18px]" />
              </Button>
            </span>
          </SimpleTooltip>
        </div>
      ),
    },
  ];

  if (showPreview && pdfData) {
    return (
      <PDFFullPagePreview
        pdfBlob={pdfData.blob}
        fileName={pdfData.fileName}
        onClose={() => setShowPreview(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-muted-foreground">Manage and track your invoices</p>
        </div>
        <Button asChild>
          <Link to="/invoices/new">
            <Plus className="mr-2 h-4 w-4" />
            New Invoice
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {/* Search */}
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                type="search"
                placeholder="Search by invoice no. or customer..."
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <SelectDropdown
                value={statusFilter}
                options={[
                  { value: 'all_status', label: 'All Status' },
                  { value: 'draft', label: 'Draft' },
                  ...invoiceStatusOptions,
                ]}
                placeholder="All Status"
                onChange={(val) => setStatusFilter(val)}
              />
            </div>

            {/* Sort */}
            <div className="space-y-2">
              <Label>Sort By</Label>
              <SelectDropdown
                value={sortValue}
                options={invoiceSortOptions}
                onChange={(val) => {
                  setSortValue(val);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 rounded-lg border p-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : totalInvoices === 0 ? (
            <div className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-semibold">No invoices found</h3>
              <p className="mb-4 text-muted-foreground">
                {search || statusFilter
                  ? 'Try different search or filter criteria'
                  : 'Get started by creating your first invoice'}
              </p>
              <Button asChild>
                <Link to="/invoices/new">
                  <Plus className="mr-2 h-4 w-4" />
                  New Invoice
                </Link>
              </Button>
            </div>
          ) : (
            <DataTable
              data={invoices}
              columns={columns}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              total={totalInvoices}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={setRowsPerPage}
              emptyMessage="No invoices found"
            />
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent>
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <h4 className="mb-2 text-sm font-medium text-primary/90">Invoice Information</h4>
              <div className="space-y-1 text-sm text-primary/80">
                <p>
                  <strong>Draft:</strong> Invoices saved as draft are not sent to customers.
                </p>
                <p>
                  <strong>Sent:</strong> Invoices that have been sent and are awaiting payment.
                </p>
                <p>
                  <strong>Paid:</strong> Invoices that have been fully paid by the customer.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        entityName={invoiceToDelete ? `Invoice #${invoiceToDelete.invoiceNumber}` : undefined}
        loading={deleting}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default InvoiceList;
